import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { DataTableConfig, DataTableModule } from '@qtek/libs/data-table';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  ItemActions,
  NewRelationsActions,
  SchemaEditorActions,
  selectTasksLoadingByMysid,
  TASK_FORM_MYSID,
  TASK_MYSID,
  TASK_MYSID_TOKEN,
  TasksActions,
  TasksFeature,
} from '@qtek/libs/store';
import { TaskTableStatusComponent } from '@qtek/libs/tasks';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import { Column, TableTask, Task, TaskParams } from '@qtek/shared/models';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { TaskDisplayRow, TaskListRow, TaskListTask } from './task-list.types';

@Component({
  selector: 'qt-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslationCoreModule,
    DataTableModule,
    MatSlideToggleModule,
    TaskTableStatusComponent,
  ],
  providers: [
    { provide: TASK_MYSID_TOKEN, useValue: TASK_MYSID.PERSONAL_TASKS },
  ],
})
export class TaskListComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private dialogLoader = inject(LazyDialogLoader);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslationCoreService);
  private mysid = inject(TASK_MYSID_TOKEN);

  @Output() expandStateChange = new EventEmitter<boolean>();
  @Output() taskClick = new EventEmitter<Task>();

  isTaskListExpanded = signal<boolean>(true);
  private groupByDealSubject = new BehaviorSubject<boolean>(true);
  readonly groupByDeal$ = this.groupByDealSubject.asObservable();

  tasks$: Observable<TaskListTask[]> = this.store
    .select(TasksFeature.selectTasksByMysid(this.mysid))
    .pipe(
      map(
        tasks => tasks.filter(task => task.status === 'todo') as TaskListTask[]
      )
    );

  isLoading = toSignal(
    this.store.select(selectTasksLoadingByMysid(this.mysid))
  );

  displayedTasks$ = combineLatest([this.tasks$, this.groupByDeal$]).pipe(
    map(([tasks, groupByDeal]) => this.mapTaskRows(tasks, groupByDeal))
  );

  rowStylesCallback = (row: TaskListRow) =>
    row.kind === 'group'
      ? {
          'background-color': 'var(--app-power-search-surface-background)',
          color: 'var(--app-main-text)',
          'font-weight': '600',
          'min-height': '52px',
          display: 'grid',
          'grid-template-columns': '1fr 0 0',
          'grid-column': '1 / -1',
        }
      : {};

  taskTableColumns: Column[] = [
    {
      id: 'title',
      label: 'GNR_TITLE',
      minWidth: 120,
      maxWidth: 300,
      hideOrder: 0,
    },
    {
      id: 'dueDate',
      label: 'GNR_EXECUTE_DATE',
      minWidth: 120,
      maxWidth: 180,
      hideOrder: 1,
    },
    {
      id: 'status',
      label: 'GNR_STATUS',
      minWidth: 90,
      maxWidth: 170,
      hideOrder: 2,
    },
  ];

  dataTableConfig: Partial<DataTableConfig> = {
    selectable: false,
    hideSelect: true,
    menuButIcon: false,
    draggable: false,
    droppable: false,
    multiple: false,
    customGridColumns: 'repeat(3, minmax(0, auto))',
  };

  ngOnInit(): void {
    this.subscribeFormData();
    this.subscribeToTasks();
  }

  ngOnDestroy(): void {
    this.store.dispatch(TasksActions.unsubscribeTasks({ mysid: this.mysid }));
    this.unsubscribeSchemas();
    this.unsubscribeFormData();
  }

  onTaskClick(task: Task): void {
    if (task.expired) {
      const data: ErrorSnackBarData = {
        type: 'ErrorMessage',
        message: this.translate.instant('ERR_TASK_EXPIRED'),
      };
      this.snackBar.showSnackBar(data);
      return;
    }

    if (this.isUploadFileTask(task) && task.params?.['tkn']?.[0]) {
      this.openFileUploaderDialog(task);
    } else {
      this.openTaskDialog(task);
    }
  }

  openTaskDialog(task?: Task): void {
    this.dialogLoader
      .open('tasks-dialog', {
        mode: task ? 'edit' : 'create',
        ...(task ? { ownTask: true } : {}),
        taskId: task?._id || null,
      })
      .subscribe();
  }

  private subscribeToTasks(): void {
    const payload = {
      view: 'pln',
      cmd: 'query' as const,
      prms: {
        ownerType: 'prs',
        status: 'todo',
      },
      s: 'model',
    };

    this.store.dispatch(
      TasksActions.subscribeTasks({ payload, mysid: this.mysid })
    );
  }

  private subscribeFormData(): void {
    this.store.dispatch(
      NewRelationsActions.subscribePeople({
        mysid: TASK_FORM_MYSID.ALL_CONTACTS,
        view: 'prs-basic',
        q: [
          {
            k: 'mysid',
            op: 'EQ',
            v: ['task_view_people'],
          },
        ],
        params: {
          cmd: 'query',
          s: 'model',
        },
      })
    );

    this.store.dispatch(
      ItemActions.subscribeItem({
        mysid: TASK_FORM_MYSID.ORIGINATIONS_DEALS,
        view: 'right_panel',
        q: [{ k: 'stg', op: 'NQ', v: [500, 600] }],
      })
    );
  }

  private unsubscribeSchemas(): void {
    this.store.dispatch(
      SchemaEditorActions.disconnectSchemas({
        mysid: 'task-form-schemas',
      })
    );
  }

  private unsubscribeFormData(): void {
    this.store.dispatch(
      ItemActions.unsubscribeItem({
        mysid: TASK_FORM_MYSID.ORIGINATIONS_DEALS,
      })
    );

    this.store.dispatch(
      NewRelationsActions.unsubscribePeople({
        mysid: TASK_FORM_MYSID.ALL_CONTACTS,
      })
    );
  }

  private isUploadFileTask(task: Task): boolean {
    return task.action === 'upload_doc';
  }

  private openFileUploaderDialog(task: Task): void {
    this.dialogLoader
      .open('file-uploader-dialog', {
        task: task,
        token: task.params?.['token'] || undefined,
      })
      .subscribe();
  }

  onGroupByDealToggle(value: boolean): void {
    this.groupByDealSubject.next(value);
  }

  onRowClick(row: TaskListRow): void {
    if (row.kind === 'task') {
      this.onTaskClick(row.task);
    }
  }

  onTaskStatusChange(
    event: { task: TableTask; status: string },
    row: TaskListRow
  ): void {
    if (row.kind !== 'task') {
      return;
    }

    const fallbackExecuteDate = row.dueDate
      ? row.dueDate.toISOString().split('T')[0]
      : null;
    const executeDate =
      event.task.executeDate ?? row.executeDate ?? fallbackExecuteDate;

    const updatePayload = {
      ownerType: event.task.ownerType,
      status: event.status,
      action: event.task.action,
      refId: event.task.refId || event.task.params.refId?.[0] || null,
      params: event.task.params,
      executeDate,
      usrId: event.task.usrId,
    };

    this.store.dispatch(
      TasksActions.updateTask({
        payload: updatePayload,
        mysid: this.mysid,
        id: event.task._id,
      })
    );
  }

  trackByRow(_index: number, row: TaskListRow): string {
    return row.id;
  }

  getStatusValue(row: TaskListRow): 'todo' | 'done' | 'pending' {
    if (row.kind !== 'task') {
      return 'todo';
    }
    const status = row.status;
    if (status === 'done' || status === 'pending') {
      return status;
    }
    return 'todo';
  }

  private mapTaskRows(
    tasks: TaskListTask[],
    groupByDeal: boolean
  ): TaskListRow[] {
    const normalized = tasks.map(task => this.toDisplayRow(task));
    if (!groupByDeal) {
      return normalized;
    }

    const noDealLabel = this.translate.instant('GNR_NO_DEAL');
    const grouped = new Map<string, TaskDisplayRow[]>();
    normalized.forEach(row => {
      const key = row.dealName || noDealLabel;
      const groupRows = grouped.get(key) ?? [];
      groupRows.push(row);
      grouped.set(key, groupRows);
    });

    const result: TaskListRow[] = [];
    Array.from(grouped.entries())
      .sort(([a], [b]) => {
        if (a === noDealLabel && b !== noDealLabel) {
          return -1;
        }
        if (a !== noDealLabel && b === noDealLabel) {
          return 1;
        }
        return a.localeCompare(b);
      })
      .forEach(([label, rows]) => {
        result.push({ kind: 'group', id: `group-${label}`, label });
        result.push(...rows);
      });

    return result;
  }

  private toDisplayRow(task: TaskListTask): TaskDisplayRow {
    const params: TaskParams = task.params ?? ({} as TaskParams);
    const title =
      params['title']?.[0] ||
      params['dealName']?.[0] ||
      this.getItemInfoName(task) ||
      task.action ||
      '';
    const dealName =
      params['dealName']?.[0] || this.getItemInfoName(task) || '';
    const dueDateString =
      task.executeDate ??
      params['dueDate']?.[0] ??
      params['deadline']?.[0] ??
      null;
    const dueDate = dueDateString ? new Date(dueDateString) : null;

    return {
      kind: 'task',
      id: task._id,
      title,
      dealName,
      dueDate,
      executeDate: dueDateString,
      status: task.status || 'todo',
      task,
      expired: !!task.expired,
    };
  }

  private getItemInfoName(task: Task): string | undefined {
    return (task as any)?.itemInfo?.name;
  }
}
